import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  Table,
  TextInput
} from '@mantine/core';
import {
  ChevronRight,
  Clock,
  CurrencyDollar,
  Mail,
  MailOff,
  X
} from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import {
  useGlobalFormState,
  useMediaQueries,
  useModalState
} from '../../../helpers/hooks';
import {
  CURRENCY_TYPE_ENUM,
  CURRENCY_TYPE_LIST,
  PAYMENT_PROVIDER_LIST,
  PAYMENT_PROVIDERS
} from '../../../config/constants';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import AppImage from '../../common/AppImage';
import FinancialPayeeOnboardingModal from '../financials/FinancialPayeeOnboardingModal';
import SettingSelector from '../../common/SettingSelector';
import ChangeCurrencyModal from './ChangeCurrencyModal';
import { triggerNotification } from '../../../helpers/notificationHelper';
import SettingCard from './SettingCard';
import TreasurerEmailSelectModal from './TreasurerEmailSelectModal';
import ConfirmModal from '../../common/ConfirmModal';
import { currencyFormat } from '../../../helpers/format';

const INITIAL_FORM_STATE = {
  treasurerEmailDisabled: false,
  treasurerAlertsEmail: '',
  fkCurrencyType: null,
  supportEmail: null
};

const AssociationPayeeSettingsView = ({ regAssociation }) => {
  const { changeAssociation } = useContext(RegistrationAdminDashboardContext);
  const {
    fetchAdminRegAssociations,
    updateRegAssociation,
    disconnectPaymentProvider
  } = useContext(RegistrationAdminContext);
  const { isMobileOrSmaller, isTabletOrSmaller } = useMediaQueries();
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const {
    hasInitialized,
    hasUnsavedChanges,
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState,
    ConfirmDiscardModal
  } = useGlobalFormState(INITIAL_FORM_STATE, {
    confirmDiscard: true,
    containerWidth: 800
  });
  const paymentProvider = PAYMENT_PROVIDER_LIST.find(
    (p) =>
      p.value ===
      regAssociation?.regAssociationPaymentProvider?.fkPaymentProvider
  );

  const paymentProviderStatusInfo = !regAssociation?.regAssociationPaymentProvider
    ? {
        label: 'Not connected',
        color: '#c40000'
      }
    : regAssociation?.regAssociationPaymentProvider &&
      regAssociation?.regAssociationPaymentProvider.completed
    ? {
        label: 'Active',
        color: 'green'
      }
    : {
        label: 'Info Missing',
        color: '#c40000'
      };

  useEffect(() => {
    if (regAssociation) {
      resetFormState({
        treasurerEmailDisabled: !regAssociation.treasurerAlertsEmail,
        treasurerAlertsEmail: regAssociation.treasurerAlertsEmail ?? '',
        fkCurrencyType:
          regAssociation.regAssociationPaymentProvider?.fkCurrencyType?.toString() ??
          CURRENCY_TYPE_ENUM.CAD.toString(),
        supportEmail: regAssociation.supportEmail ?? ''
      });
    }
  }, [regAssociation]);

  const onSaveChanges = () => {
    submitFormState((formData, onErrorCallback) => {
      updateRegAssociation(
        regAssociation.pkRegAssociation,
        {
          ...regAssociation,
          ...formState,
          treasurerAlertsEmail: formState.treasurerEmailDisabled
            ? null
            : formState.treasurerAlertsEmail
        },
        () => {
          triggerNotification('Association Updated', 'Success', 'green');
        },
        onErrorCallback
      );
    });
  };

  const getFeeDescription = (percentageFee, flatFee) =>
    `${percentageFee}%${
      flatFee > 0 ? ` + ${currencyFormat(flatFee / 100)}` : ''
    } per transaction`;

  return (
    <AppStack
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSaveChanges();
      }}
      style={{
        gap: 20,
        padding: 8,
        paddingBottom: 20,
        maxHeight: '100%',
        overflow: 'auto'
      }}
    >
      <AppFlexbox
        style={{
          gap: 16,
          justifyContent: 'space-between',
          alignItems: isMobileOrSmaller ? 'start' : 'center',
          flexDirection: isMobileOrSmaller ? 'column' : 'row'
        }}
      >
        <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
          <AppText style={{ fontSize: 20, fontWeight: 500 }}>
            Payment Provider
          </AppText>

          <Badge color={paymentProviderStatusInfo.color} variant="filled">
            {paymentProviderStatusInfo.label}
          </Badge>
        </AppFlexbox>

        <AppFlexbox
          style={{
            gap: 8,
            alignItems: 'center',
            flex: isMobileOrSmaller ? 1 : 'unset',
            width: isMobileOrSmaller ? '100%' : 'unset'
          }}
        >
          <Button
            color="red"
            onClick={() => onOpenModal('REMOVE_PROVIDER', regAssociation)}
            rightSection={<X size={18} />}
            size="compact-sm"
            style={{ flex: 1, width: isMobileOrSmaller ? 1 : 'unset' }}
            variant={isMobileOrSmaller ? 'filled' : 'subtle'}
          >
            Disconnect
          </Button>
        </AppFlexbox>
      </AppFlexbox>
      <SettingSelector
        description={regAssociation?.regAssociationPaymentProvider?.accountId}
        disabled={false}
        leftSection={
          <AppStack
            style={{
              width: '100%',
              height: '100%',
              maxWidth: 50,
              maxHeight: 50,
              borderRadius: 100,
              overflow: 'hidden'
            }}
          >
            <AppImage src={paymentProvider?.image} />
          </AppStack>
        }
        onClick={() => onOpenModal('ONBOARD')}
        rightSection={
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            {!isMobileOrSmaller && (
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                Click to edit
              </AppText>
            )}
            <ChevronRight size={18} />
          </AppFlexbox>
        }
        subDescription={
          <AppStack style={{ gap: 0 }}>
            {paymentProvider && regAssociation && (
              <AppText style={{ fontSize: 14, color: '#666' }}>
                {paymentProvider.canCustomizeServiceFee
                  ? getFeeDescription(
                      regAssociation.regAssociationPaymentProvider
                        .percentageRateFee ||
                        paymentProvider.defaultPercentageServiceFee,
                      regAssociation.regAssociationPaymentProvider
                        .flatRateFee ||
                        paymentProvider.defaultFlatServiceFee * 100
                    )
                  : paymentProvider.serviceFeeDescription}
              </AppText>
            )}

            <AppText
              style={{ fontSize: 14, color: paymentProviderStatusInfo.color }}
            >
              {paymentProviderStatusInfo.label}
            </AppText>
          </AppStack>
        }
        title={paymentProvider?.label}
        withBorder
      />

      <Divider />

      {!regAssociation.supportEmail && (
        <SettingCard
          description="Set up the contact email for your association. This email will serve as the primary point of contact for official communications and inquiries related to the registration process."
          title="Contact Information"
        >
          <AppFlexbox style={{ flex: 1 }}>
            <TextInput
              disabled={isSubmitting}
              label="Support Email"
              onChange={(e) =>
                setFormState({
                  ...formState,
                  supportEmail: e.currentTarget.value
                })
              }
              placeholder={`support@${regAssociation?.association.name
                .replace(/\s/g, '')
                .toLowerCase()}.com`}
              required
              style={{ flex: 1 }}
              value={formState.supportEmail ?? ''}
            />
            <AppStack style={{ flex: 1 }} />
          </AppFlexbox>
        </SettingCard>
      )}

      <AppStack style={{ gap: 10 }}>
        <AppStack style={{ gap: 5 }}>
          <AppText style={{ fontWeight: 500 }}>Currency</AppText>
          <AppText style={{ fontSize: 14, color: '#666' }}>
            Select the preferred currency for your association. This will be
            used for all online payments and transactions.
          </AppText>
        </AppStack>

        <SettingSelector
          {...(paymentProvider?.value === PAYMENT_PROVIDERS.sportspay.value
            ? {
                disabled: true,
                disabledMessage:
                  'Currency cannot be changed for this payment provider'
              }
            : {})}
          leftSection={<CurrencyDollar size={32} />}
          onClick={() => onOpenModal('CHANGE_CURRENCY')}
          rightSection={
            <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
              {!isMobileOrSmaller && (
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  Click to edit
                </AppText>
              )}
              <ChevronRight size={18} />
            </AppFlexbox>
          }
          title={
            CURRENCY_TYPE_LIST.find(
              (currency) =>
                currency.value.toString() ===
                formState.fkCurrencyType?.toString()
            )?.fullLabel
          }
          withBorder
        />
      </AppStack>

      <AppStack style={{ gap: 10 }}>
        <AppStack style={{ gap: 5 }}>
          <AppText style={{ fontWeight: 500 }}>Treasurer Email</AppText>
          <AppText style={{ fontSize: 14, color: '#666' }}>
            Choose where to receive email alerts when online purchases are made.
          </AppText>
        </AppStack>

        <SettingSelector
          description={
            !formState.treasurerAlertsEmail
              ? 'Not setup'
              : formState.treasurerEmailDisabled
              ? 'Disabled'
              : 'Active'
          }
          descriptionColor={
            !formState.treasurerAlertsEmail || formState.treasurerEmailDisabled
              ? '#c40000'
              : '#067D62'
          }
          disabled={formState.loading}
          leftSection={
            formState.treasurerAlertsEmail &&
            !formState.treasurerEmailDisabled ? (
              <Mail color="#067D62" size={32} />
            ) : (
              <MailOff color="#c40000" size={32} />
            )
          }
          onClick={() => {
            onOpenModal('treasurer_email');
          }}
          rightSection={
            <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
              {!isMobileOrSmaller && (
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  {formState.treasurerAlertsEmail
                    ? 'Click to change'
                    : 'Click to setup'}
                </AppText>
              )}
              <ChevronRight size={18} />
            </AppFlexbox>
          }
          title={formState.treasurerAlertsEmail || 'Setup a treasurer email'}
          withBorder
        />
      </AppStack>

      {paymentProvider?.canCustomizeInstallments && (
        <AppStack style={{ gap: 10 }}>
          <AppStack style={{ gap: 5 }}>
            <AppText style={{ fontWeight: 500 }}>Payment plans</AppText>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              Set up payment plans for your association, allowing members to pay
              in installments at scheduled intervals.
            </AppText>
          </AppStack>

          <SettingSelector
            component={Link}
            description="Allow members to pay in installments by set intervals."
            descriptionColor="#666"
            leftSection={<Clock color="#000" size={32} />}
            onClick={() => {}}
            rightSection={
              <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
                {!isMobileOrSmaller && (
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Click to manage
                  </AppText>
                )}

                <ChevronRight size={18} />
              </AppFlexbox>
            }
            title="Manage payment plans"
            to={`/admin/settings/${regAssociation?.pkRegAssociation}/payment-plans`}
            withBorder
          />
        </AppStack>
      )}

      {hasUnsavedChanges && (
        <AppFlexbox
          p={{ base: 16, sm: '16px 0px' }}
          style={{ justifyContent: 'flex-end' }}
        >
          <Button
            color="dark"
            disabled={!hasUnsavedChanges}
            id="save-button"
            loading={isSubmitting}
            radius="md"
            size={isTabletOrSmaller ? 'sm' : 'compact-sm'}
            type="submit"
            variant="filled"
          >
            Save changes
          </Button>
        </AppFlexbox>
      )}

      {ConfirmDiscardModal}

      <FinancialPayeeOnboardingModal
        isOpen={modalState.isOpen && modalState.action === 'ONBOARD'}
        onClose={onCloseModal}
        regAssociation={regAssociation}
      />

      <ChangeCurrencyModal
        currencyType={formState.fkCurrencyType}
        isOpen={modalState.isOpen && modalState.action === 'CHANGE_CURRENCY'}
        onClose={onCloseModal}
        onConfirm={(fkCurrencyType) => {
          setFormState({
            ...formState,
            fkCurrencyType,
            hasChanges: true
          });
          onCloseModal();
        }}
        regAssociation={regAssociation}
        sandboxEnabled={regAssociation?.sandboxEnabled}
        showRedirectButton
      />

      <TreasurerEmailSelectModal
        isOpen={modalState.isOpen && modalState.action === 'treasurer_email'}
        onClose={onCloseModal}
        onConfirm={({ treasurerEmailDisabled, treasurerAlertsEmail }) => {
          setFormState({
            ...formState,
            treasurerEmailDisabled,
            treasurerAlertsEmail: treasurerEmailDisabled
              ? formState.treasurerAlertsEmail
              : treasurerAlertsEmail
          });
          onCloseModal();
        }}
        resource={formState}
      />

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, remove payee"
        isLoading={modalState.loading}
        isNewModalStyle
        isOpen={modalState.isOpen && modalState.action === 'REMOVE_PROVIDER'}
        message={
          <AppStack style={{ gap: 20 }}>
            <AppText style={{ textAlign: 'center' }}>
              Are you sure you want to remove this payee from{' '}
              <b>{modalState.item?.association.name}</b>?
            </AppText>

            <AppText style={{ fontSize: 14 }}>
              This action will disconnect the payment provider from{' '}
              <b>{modalState.item?.association.name}</b>. As a result, the
              association will no longer be able to process online payments, and
              all registrations will become unavailable for purchase until a
              payee is connected again.
            </AppText>

            <Divider label="Confirmation" labelPosition="center" />

            <Checkbox
              disabled={modalState.loading}
              label="I understand that removing this payee will disable online payments and make all registrations unavailable for purchase until a new payee is added."
              required
            />
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          disconnectPaymentProvider(
            modalState.item?.pkRegAssociation,
            () => {
              fetchAdminRegAssociations((data) => {
                const updatedRegAssociation = data.find(
                  (s) =>
                    s.pkRegAssociation === modalState.item?.pkRegAssociation
                );
                changeAssociation({
                  pkRegAssociation: updatedRegAssociation.pkRegAssociation,
                  regAssociation: updatedRegAssociation
                });
              });

              onChangeModalLoading(false);
              triggerNotification('Payee removed!', 'Success', 'green');
              onCloseModal();
            },
            (message) => {
              triggerNotification(message);
              onChangeModalLoading(false);
            }
          );
        }}
        title="Remove payee?"
        topPadding={0}
      />
    </AppStack>
  );
};

AssociationPayeeSettingsView.propTypes = { regAssociation: PropTypes.object };

export default AssociationPayeeSettingsView;
